/* bullard-viewer360.js — Bullard Viewer360: look-around "See It On Your Vehicle".
 *
 * Phase 1 proof build (see ~/workspace/design-studio/DESIGN_360_viewer.md).
 * Vanilla JS, WebGL1, no libraries, no build step. One file: the pure math core
 * (Node-importable, UMD-ish guard at the bottom, same pattern as spike_math.js)
 * plus the browser viewer behind window.BullardViewer360.mount().
 *
 * Anchoring model (spec D3 / section 4): decals live on physical panel PLANES
 * in station space, in panel-local inches, and are reprojected every frame
 * with the same pinhole camera as the panorama — never painted into the pano.
 *
 * DEVIATION for this proof (per stage2/station.json + GEOMETRY.md): panels
 * arrive PRE-SOLVED — plane.center / uAxis / vAxis / normal are consumed
 * directly through panelPoint(); solvePanel runs at authoring time only and
 * is therefore not shipped here.
 *
 * Conventions (spec section 4.2, identical to spike_math.js):
 * - World frame: right-handed, +Y up, station (camera) at origin.
 * - Direction (yawDeg, pitchDeg): yaw 0 = +Z, positive yaw toward +X,
 *   pitch positive = up. dir = (cos p * sin y, sin p, cos p * cos y).
 * - Equirect UV: u = yaw/360 + 0.5, v = 0.5 - pitch/180 (v measured from the
 *   TOP of the image). Textures are uploaded with UNPACK_FLIP_Y_WEBGL left at
 *   its default (false), so texture row 0 is the image's top row and texture
 *   coordinate t == the v above. Do not "fix" this by flipping one side only.
 * - Panel-local inches: u right, v DOWN, origin at panel center.
 * - Cone values in station.cone are OFFSETS from station forward
 *   (horizon.yaw0 / horizon.pitch0); yaw0 is pano-absolute and may exceed
 *   +180 (here 183), so all yaw differences go through wrapDeg180.
 */
(function (global) {
  'use strict';

  /* ================= math core (pure, Node-importable) ================= */
  var D2R = Math.PI / 180, R2D = 180 / Math.PI;

  function clamp(x, a, b) { return x < a ? a : (x > b ? b : x); }
  function v3(x, y, z) { return [x, y, z]; }
  function add(a, b) { return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]; }
  function sub(a, b) { return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]; }
  function mul(a, s) { return [a[0] * s, a[1] * s, a[2] * s]; }
  function dot(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
  function cross(a, b) {
    return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  }
  function len(a) { return Math.sqrt(dot(a, a)); }
  function norm(a) { var l = len(a) || 1; return [a[0] / l, a[1] / l, a[2] / l]; }

  /* Normalize an angle to (-180, +180]. The panel span of this station runs
   * +160 .. -131 pano-absolute (through the seam), so every yaw difference
   * and every cone comparison must pass through here. */
  function wrapDeg180(d) {
    d = d % 360;
    if (d > 180) d -= 360;
    if (d < -180) d += 360;
    return d;
  }

  function dirFromYawPitch(yawDeg, pitchDeg) {
    var y = yawDeg * D2R, p = pitchDeg * D2R, cp = Math.cos(p);
    return v3(cp * Math.sin(y), Math.sin(p), cp * Math.cos(y));
  }
  function yawPitchFromDir(d) {
    var n = norm(d);
    return { yaw: Math.atan2(n[0], n[2]) * R2D, pitch: Math.asin(clamp(n[1], -1, 1)) * R2D };
  }
  function equirectUV(d) {
    var yp = yawPitchFromDir(d);
    var u = yp.yaw / 360 + 0.5; if (u < 0) u += 1; if (u >= 1) u -= 1;
    return { u: u, v: 0.5 - yp.pitch / 180 };
  }
  function angleBetweenDeg(a, b) {
    return Math.acos(clamp(dot(norm(a), norm(b)), -1, 1)) * R2D;
  }

  /* Intersect ray r with plane n.p = d (d > 0). Returns point or null. */
  function rayPlane(r, n, d) {
    var dn = dot(n, r);
    if (dn < 1e-9) return null;
    return mul(r, d / dn);
  }

  /* Panel-local inches -> 3D point on a pre-solved plane (spec 4.2).
   * panel = { center, uAxis, vAxis } with unit axes, v down. */
  function panelPoint(panel, uIn, vIn) {
    return add(add(panel.center, mul(panel.uAxis, uIn)), mul(panel.vAxis, vIn));
  }

  /* Subdivided decal mesh points on the panel (spec 4.1: 8x8 grid; the
   * subdivision keeps the same geometry valid for the PNG exporter and any
   * future canvas fallback, and costs nothing). Each point carries its
   * artwork uv: u 0..1 left->right, v 0..1 TOP->bottom of the panel, which
   * matches the unflipped texture upload (art top lands on panel top). */
  function decalGrid(panel, uIn, vIn, wIn, hIn, rotDeg, seg) {
    seg = seg || 8;
    var th = (rotDeg || 0) * D2R, c = Math.cos(th), s = Math.sin(th);
    var pts = [], i, j;
    for (j = 0; j <= seg; j++) {
      for (i = 0; i <= seg; i++) {
        var lu = (i / seg - 0.5) * wIn, lv = (j / seg - 0.5) * hIn;
        var ru = lu * c - lv * s, rv = lu * s + lv * c;
        pts.push({ p: panelPoint(panel, uIn + ru, vIn + rv), u: i / seg, v: j / seg });
      }
    }
    return { pts: pts, seg: seg };
  }

  /* View basis shared by project() and viewRayDir() so picking rays and the
   * render projection can never disagree. */
  function viewBasis(yawDeg, pitchDeg) {
    var fwd = dirFromYawPitch(yawDeg, pitchDeg);
    var right = norm(cross(fwd, [0, 1, 0]));
    var up = cross(right, fwd);
    return { fwd: fwd, right: right, up: up };
  }

  /* Pinhole projection (identical math to the GL matrices in the viewer).
   * view = {yaw, pitch, fovYDeg, aspect, width, height}; yaw/pitch absolute. */
  function project(p, view) {
    var b = viewBasis(view.yaw, view.pitch);
    var zc = dot(p, b.fwd); if (zc <= 1e-6) return null;
    var xc = dot(p, b.right), yc = dot(p, b.up);
    var tanY = Math.tan(view.fovYDeg * D2R / 2), tanX = tanY * view.aspect;
    var ndcX = (xc / zc) / tanX, ndcY = (yc / zc) / tanY;
    return { x: (ndcX + 1) / 2 * view.width, y: (1 - ndcY) / 2 * view.height, ndcX: ndcX, ndcY: ndcY, z: zc };
  }

  /* Inverse of project(): the world ray through an NDC point, for picking. */
  function viewRayDir(yawDeg, pitchDeg, fovYDeg, aspect, ndcX, ndcY) {
    var b = viewBasis(yawDeg, pitchDeg);
    var tanY = Math.tan(fovYDeg * D2R / 2), tanX = tanY * aspect;
    return norm(add(add(b.fwd, mul(b.right, ndcX * tanX)), mul(b.up, ndcY * tanY)));
  }

  /* Station config panel -> runtime panel (pre-solved plane, see header). */
  function panelFromConfig(cfg) {
    return {
      id: cfg.id, label: cfg.label || cfg.id,
      center: cfg.plane.center.slice(), uAxis: cfg.plane.uAxis.slice(),
      vAxis: cfg.plane.vAxis.slice(), normal: cfg.plane.normal.slice(),
      widthIn: cfg.widthIn, heightIn: cfg.heightIn,
      fitPolygonIn: cfg.fitPolygonIn || null
    };
  }

  /* Ray -> decal's panel plane -> panel-local inches (spec 4.4 move math). */
  function intersectPanel(rayDir, panel) {
    var d = dot(panel.normal, panel.center);
    var p = rayPlane(rayDir, panel.normal, d);
    if (!p) return null;
    var rel = sub(p, panel.center);
    return { point: p, u: dot(rel, panel.uAxis), v: dot(rel, panel.vAxis), t: len(p) };
  }

  /* View <-> cone-relative conversion. Cone bounds are offsets from station
   * forward (spec 5.1), so the viewer stores the view RELATIVE and converts
   * through yaw0/pitch0 — with wrap — whenever absolute angles are needed. */
  function relFromAbs(station, yawAbs, pitchAbs) {
    return {
      relYaw: wrapDeg180(yawAbs - station.horizon.yaw0),
      relPitch: pitchAbs - station.horizon.pitch0
    };
  }
  function absFromRel(station, relYaw, relPitch) {
    return {
      yaw: wrapDeg180(station.horizon.yaw0 + relYaw),
      pitch: station.horizon.pitch0 + relPitch
    };
  }
  function coneClampRel(station, relYaw, relPitch) {
    var c = station.cone;
    return {
      relYaw: clamp(relYaw, c.yawMin, c.yawMax),
      relPitch: clamp(relPitch, c.pitchMin, c.pitchMax)
    };
  }

  /* Cone-relative direction of a panel's center (spec 5.3: on a station
   * switch with no saved view, the new station opens looking at the
   * counterpart panel's center). Takes the raw station config + panel
   * config (plane.center), not runtime panels. */
  function panelCenterRel(stationCfg, panelCfg) {
    var yp = yawPitchFromDir(panelCfg.plane.center);
    return relFromAbs(stationCfg, yp.yaw, yp.pitch);
  }

  /* The decal's 4 rotated corners in panel inches (spec 4.4 fit test). */
  function decalCornersIn(uIn, vIn, wIn, hIn, rotDeg) {
    var th = (rotDeg || 0) * D2R, c = Math.cos(th), s = Math.sin(th);
    var out = [], i;
    var local = [[-wIn / 2, -hIn / 2], [wIn / 2, -hIn / 2], [wIn / 2, hIn / 2], [-wIn / 2, hIn / 2]];
    for (i = 0; i < 4; i++) {
      var lu = local[i][0], lv = local[i][1];
      out.push([uIn + lu * c - lv * s, vIn + lu * s + lv * c]);
    }
    return out;
  }
  function pointInPolygon(u, v, poly) {
    var inside = false, i, j;
    for (i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      var xi = poly[i][0], yi = poly[i][1], xj = poly[j][0], yj = poly[j][1];
      if (((yi > v) !== (yj > v)) && (u < (xj - xi) * (v - yi) / (yj - yi) + xi)) inside = !inside;
    }
    return inside;
  }
  /* Fit = every rotated corner inside the panel rectangle (or the optional
   * inner fit polygon). Advisory only (spec 4.4 / section 17 contract):
   * callers report it; nothing ever blocks on it. */
  function decalFitOk(panel, uIn, vIn, wIn, hIn, rotDeg) {
    var corners = decalCornersIn(uIn, vIn, wIn, hIn, rotDeg), i;
    for (i = 0; i < 4; i++) {
      var u = corners[i][0], v = corners[i][1];
      if (panel.fitPolygonIn && panel.fitPolygonIn.length >= 3) {
        if (!pointInPolygon(u, v, panel.fitPolygonIn)) return false;
      } else if (Math.abs(u) > panel.widthIn / 2 + 1e-9 || Math.abs(v) > panel.heightIn / 2 + 1e-9) {
        return false;
      }
    }
    return true;
  }

  var Math360 = {
    clamp: clamp, wrapDeg180: wrapDeg180,
    dirFromYawPitch: dirFromYawPitch, yawPitchFromDir: yawPitchFromDir,
    equirectUV: equirectUV, angleBetweenDeg: angleBetweenDeg,
    rayPlane: rayPlane, panelPoint: panelPoint, decalGrid: decalGrid,
    viewBasis: viewBasis, project: project, viewRayDir: viewRayDir,
    panelFromConfig: panelFromConfig, intersectPanel: intersectPanel,
    relFromAbs: relFromAbs, absFromRel: absFromRel, coneClampRel: coneClampRel,
    panelCenterRel: panelCenterRel,
    decalCornersIn: decalCornersIn, pointInPolygon: pointInPolygon,
    decalFitOk: decalFitOk,
    _v: { add: add, sub: sub, mul: mul, dot: dot, cross: cross, len: len, norm: norm }
  };

  /* ================= viewer (browser only) ================= */
  var FOV_DEFAULT = 55, FOV_MIN = 38, FOV_MAX = 70;   // spec 3.3 / 3.4
  var DPR_CAP = 1.75;                                  // spec 3.3
  var SPHERE_R = 400, SPHERE_SEGX = 64, SPHERE_SEGY = 48; // spec 3.2
  var DECAL_SEG = 8;

  function mount(container, opts) {
    opts = opts || {};
    if (typeof container === 'string') container = document.getElementById(container);
    var onState = typeof opts.onState === 'function' ? opts.onState : function () {};
    /* Phase 2: mount takes opts.stations = [{station, panoUrl, posterUrl}]
     * (single-station opts.station still works, wrapped as one entry). */
    var entries = [];
    if (opts.stations && opts.stations.length) entries = opts.stations.slice();
    else if (opts.station) entries = [{ station: opts.station, panoUrl: opts.panoUrl, posterUrl: opts.posterUrl }];
    if (!container || !entries.length || !entries[0].station || !entries[0].station.panels || !entries[0].station.panels.length) {
      onState({ webgl: false, error: 'mount: container or station config missing' });
      return { setSize: function () {}, setPanel: function () { return false; },
               setStation: function () { return false; }, setDesign: function () {},
               restorePlacement: function () { return false; },
               getPlacement: function () { return null; }, getStationId: function () { return null; },
               getState: function () { return { webgl: false }; },
               exportPNG: function () { return null; }, destroy: function () {} };
    }
    var entryById = {};
    entries.forEach(function (e) { entryById[e.station.id] = e; });
    var curEntry = entryById[opts.stationId] || entries[0];
    var station = curEntry.station;
    var panels = station.panels.map(panelFromConfig);
    var panelById = {};
    panels.forEach(function (p) { panelById[p.id] = p; });
    var savedByStation = {};   // stationId -> placement+view snapshot (spec 5.3)
    var texCache = {};         // stationId -> {tex, loaded}

    /* --- DOM: poster underlay (first paint + fallback visual), canvas on top */
    container.style.position = container.style.position || 'relative';
    container.style.overflow = 'hidden';
    var posterImg = null;
    function showPoster(url) {
      if (!url) return;
      if (!posterImg) {
        posterImg = document.createElement('img');
        posterImg.alt = '';
        posterImg.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;transition:opacity .25s;';
        container.insertBefore(posterImg, canvas || null);
      }
      posterImg.src = url;
      posterImg.style.opacity = '1';
    }
    function fadePoster() {
      if (!posterImg) return;
      var img = posterImg;
      img.style.opacity = '0';
      setTimeout(function () {
        if (img.parentNode) img.parentNode.removeChild(img);
        if (posterImg === img) posterImg = null;
      }, 300);
    }
    var canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;touch-action:none;cursor:grab;';
    container.appendChild(canvas);
    showPoster(curEntry.posterUrl);

    /* --- state: decal placement in panel inches (spec 4.1) + cone-relative view */
    var state = {
      webgl: true, panoLoaded: false,
      panelId: (opts.initialPanelId && panelById[opts.initialPanelId]) ? opts.initialPanelId : panels[0].id,
      uIn: 0, vIn: 0, rotDeg: 0, wIn: 24, hIn: 12,
      relYaw: 0, relPitch: 0, fovY: FOV_DEFAULT,
      fitOk: true
    };
    var decalAspect = 2; // until the artwork's real aspect is known

    function activePanel() { return panelById[state.panelId]; }
    function absView() { return absFromRel(station, state.relYaw, state.relPitch); }

    function emit() {
      var p = activePanel(), a = absView();
      onState({
        webgl: state.webgl, panoLoaded: state.panoLoaded,
        stationId: station.id, stationLabel: station.label || station.id,
        panelId: state.panelId, panelLabel: p ? p.label : state.panelId,
        uIn: state.uIn, vIn: state.vIn, rotDeg: state.rotDeg,
        wIn: state.wIn, hIn: state.hIn, fitOk: state.fitOk,
        viewYawRel: state.relYaw, viewPitchRel: state.relPitch,
        viewYawAbs: a.yaw, viewPitchAbs: a.pitch, fovY: state.fovY
      });
    }
    function refreshFit() {
      var p = activePanel();
      state.fitOk = p ? decalFitOk(p, state.uIn, state.vIn, state.wIn, state.hIn, state.rotDeg) : false;
    }

    /* --- WebGL setup; any failure degrades to poster + note (spec 5.3) --- */
    var gl = null;
    try {
      gl = canvas.getContext('webgl', { preserveDrawingBuffer: true, antialias: true }) ||
           canvas.getContext('experimental-webgl', { preserveDrawingBuffer: true, antialias: true });
    } catch (e) { gl = null; }
    if (!gl) {
      state.webgl = false;
      onState({ webgl: false });
      return { setSize: function () {}, setPanel: function () { return false; },
               setStation: function () { return false; }, setDesign: function () {},
               restorePlacement: function () { return false; },
               getPlacement: function () { return null; }, getStationId: function () { return station.id; },
               getState: function () { return { webgl: false }; },
               exportPNG: function () { return null; }, destroy: function () {} };
    }
    canvas.addEventListener('webglcontextlost', function (e) {
      e.preventDefault();
      state.webgl = false;
      showPoster(curEntry.posterUrl);
      onState({ webgl: false, contextLost: true });
    });

    function compile(type, src) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    }
    var prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER,
      'attribute vec3 aPos; attribute vec2 aUv; varying vec2 vUv;' +
      'uniform mat4 uMvp; void main(){ vUv=aUv; gl_Position=uMvp*vec4(aPos,1.0); }'));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER,
      'precision mediump float; varying vec2 vUv; uniform sampler2D uTex;' +
      'void main(){ gl_FragColor=texture2D(uTex, vUv); }'));
    gl.linkProgram(prog); gl.useProgram(prog);
    var uMvp = gl.getUniformLocation(prog, 'uMvp'), uTex = gl.getUniformLocation(prog, 'uTex');
    var aPos = gl.getAttribLocation(prog, 'aPos'), aUv = gl.getAttribLocation(prog, 'aUv');
    gl.enableVertexAttribArray(aPos); gl.enableVertexAttribArray(aUv);
    gl.uniform1i(uTex, 0);

    function staticBuffer(data, itemSize, attr) {
      var b = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, b);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(data), gl.STATIC_DRAW);
      return { buf: b, itemSize: itemSize, attr: attr };
    }
    function indexBuffer(indices) {
      var b = gl.createBuffer();
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, b);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(indices), gl.STATIC_DRAW);
      return b;
    }

    /* --- panorama sphere: inward textured sphere, per-vertex UV in the
     *     math module's equirect convention (spec 3.2). No backface culling:
     *     the camera lives inside the sphere. */
    var spherePos = [], sphereUv = [], sphereIdx = [], si, sj;
    for (sj = 0; sj <= SPHERE_SEGY; sj++) {
      for (si = 0; si <= SPHERE_SEGX; si++) {
        var yawS = si / SPHERE_SEGX * 360 - 180, pitS = 90 - sj / SPHERE_SEGY * 180;
        var dirS = dirFromYawPitch(yawS, pitS);
        spherePos.push(dirS[0] * SPHERE_R, dirS[1] * SPHERE_R, dirS[2] * SPHERE_R);
        sphereUv.push(si / SPHERE_SEGX, sj / SPHERE_SEGY); // == (yaw/360+.5, .5-pitch/180)
      }
    }
    for (sj = 0; sj < SPHERE_SEGY; sj++) {
      for (si = 0; si < SPHERE_SEGX; si++) {
        var a0 = sj * (SPHERE_SEGX + 1) + si, b0 = a0 + 1, c0 = a0 + SPHERE_SEGX + 1, d0 = c0 + 1;
        sphereIdx.push(a0, c0, b0, b0, c0, d0);
      }
    }
    var spherePosBuf = staticBuffer(spherePos, 3, aPos);
    var sphereUvBuf = staticBuffer(sphereUv, 2, aUv);
    var sphereIdxBuf = indexBuffer(sphereIdx);

    /* --- decal mesh buffers (vertex count fixed at (seg+1)^2; positions and
     *     uvs are rewritten on every placement change) --- */
    var gridIdx = [], gi, gj;
    for (gj = 0; gj < DECAL_SEG; gj++) {
      for (gi = 0; gi < DECAL_SEG; gi++) {
        var a1 = gj * (DECAL_SEG + 1) + gi, b1 = a1 + 1, c1 = a1 + DECAL_SEG + 1, d1 = c1 + 1;
        gridIdx.push(a1, b1, c1, b1, d1, c1);
      }
    }
    var decalPosBuf = gl.createBuffer(), decalUvBuf = gl.createBuffer();
    var decalIdxBuf = indexBuffer(gridIdx);

    function rebuildDecal() {
      var p = activePanel(); if (!p) return;
      var grid = decalGrid(p, state.uIn, state.vIn, state.wIn, state.hIn, state.rotDeg, DECAL_SEG);
      var pos = new Float32Array(grid.pts.length * 3), uv = new Float32Array(grid.pts.length * 2);
      grid.pts.forEach(function (gp, i) {
        pos[i * 3] = gp.p[0]; pos[i * 3 + 1] = gp.p[1]; pos[i * 3 + 2] = gp.p[2];
        uv[i * 2] = gp.u; uv[i * 2 + 1] = gp.v;
      });
      gl.bindBuffer(gl.ARRAY_BUFFER, decalPosBuf);
      gl.bufferData(gl.ARRAY_BUFFER, pos, gl.DYNAMIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, decalUvBuf);
      gl.bufferData(gl.ARRAY_BUFFER, uv, gl.DYNAMIC_DRAW);
      refreshFit();
    }

    /* --- textures (NPOT-safe params throughout: 6144x3072 is not a power of
     *     two, so no mipmaps and CLAMP_TO_EDGE in WebGL1) --- */
    function makeTexture(source) {
      var t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      if (source) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
      return t;
    }
    var blankTex = (function () {
      var t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
        new Uint8Array([0, 0, 0, 0]));
      return t;
    })();
    var panoTex = blankTex, decalTex = blankTex;

    function loadPano(entry) {
      var sid = entry.station.id;
      if (texCache[sid] && texCache[sid].tex) {
        panoTex = texCache[sid].tex;
        state.panoLoaded = true;
        draw(); fadePoster(); emit();
        return;
      }
      if (!entry.panoUrl) { state.panoLoaded = false; return; }
      if (texCache[sid] && texCache[sid].loading) return;
      texCache[sid] = { tex: null, loaded: false, loading: true };
      var panoImg = new Image();
      panoImg.crossOrigin = 'anonymous';
      panoImg.onload = function () {
        var src = panoImg;
        var maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE);
        if (panoImg.width > maxTex) {           // spec 3.3: downscale to fit
          var dc = document.createElement('canvas');
          dc.width = maxTex; dc.height = Math.round(maxTex * panoImg.height / panoImg.width);
          dc.getContext('2d').drawImage(panoImg, 0, 0, dc.width, dc.height);
          src = dc;
        }
        var tex = makeTexture(src);
        texCache[sid] = { tex: tex, loaded: true, loading: false };
        if (station.id !== sid) return;         // user switched away meanwhile
        panoTex = tex;
        state.panoLoaded = true;
        draw(); fadePoster(); emit();
      };
      panoImg.onerror = function () {
        texCache[sid] = { tex: null, loaded: false, loading: false };
        if (station.id === sid) emit();         // poster stays up; panoLoaded:false
      };
      panoImg.src = entry.panoUrl;
    }
    function setDecalSource(src, w, h) {
      decalTex = makeTexture(src);
      if (w > 0 && h > 0) decalAspect = w / h;
      state.hIn = state.wIn / decalAspect;
      rebuildDecal(); draw(); emit();
    }
    if (opts.decalCanvas) {
      setDecalSource(opts.decalCanvas, opts.decalCanvas.width, opts.decalCanvas.height);
    } else if (opts.decalUrl) {
      var decalImg = new Image();
      decalImg.crossOrigin = 'anonymous';
      decalImg.onload = function () { setDecalSource(decalImg, decalImg.naturalWidth, decalImg.naturalHeight); };
      decalImg.src = opts.decalUrl;
    }

    /* --- minimal mat4 (column-major, same construction as the spike) --- */
    function perspective(fovYDeg, aspect, near, far) {
      var f = 1 / Math.tan(fovYDeg * D2R / 2), nf = 1 / (near - far);
      return [f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0];
    }
    function lookAt(eye, center, up) {
      var z = norm(sub(eye, center)), x = norm(cross(up, z)), y = cross(z, x);
      return [x[0], y[0], z[0], 0, x[1], y[1], z[1], 0, x[2], y[2], z[2], 0,
              -dot(x, eye), -dot(y, eye), -dot(z, eye), 1];
    }
    function multiply(a, b) {
      var o = new Array(16), c, r;
      for (c = 0; c < 4; c++) for (r = 0; r < 4; r++) {
        o[c * 4 + r] = a[r] * b[c * 4] + a[4 + r] * b[c * 4 + 1] + a[8 + r] * b[c * 4 + 2] + a[12 + r] * b[c * 4 + 3];
      }
      return o;
    }

    function fitCanvas() {
      var dpr = Math.min(global.devicePixelRatio || 1, DPR_CAP);
      var w = Math.max(2, Math.round(canvas.clientWidth * dpr));
      var h = Math.max(2, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
    }

    /* Render on demand ONLY (spec 3.3): called from input handlers, state
     * changes, the ease-back animation, and texture loads. No idle rAF loop. */
    function draw() {
      if (!state.webgl) return;
      var w = canvas.width, h = canvas.height;
      gl.viewport(0, 0, w, h);
      gl.clearColor(0.04, 0.04, 0.04, 1);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.enable(gl.DEPTH_TEST);
      gl.disable(gl.BLEND);
      var a = absView();
      var fwd = dirFromYawPitch(a.yaw, a.pitch);
      var mvp = multiply(perspective(state.fovY, w / h, 1, 900), lookAt([0, 0, 0], fwd, [0, 1, 0]));
      gl.uniformMatrix4fv(uMvp, false, new Float32Array(mvp));
      // panorama sphere
      gl.bindBuffer(gl.ARRAY_BUFFER, spherePosBuf.buf);
      gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, sphereUvBuf.buf);
      gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 0, 0);
      gl.bindTexture(gl.TEXTURE_2D, panoTex);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, sphereIdxBuf);
      gl.drawElements(gl.TRIANGLES, sphereIdx.length, gl.UNSIGNED_SHORT, 0);
      // decal on its panel plane (alpha-blended)
      if (activePanel()) {
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        gl.bindBuffer(gl.ARRAY_BUFFER, decalPosBuf);
        gl.vertexAttribPointer(aPos, 3, gl.FLOAT, false, 0, 0);
        gl.bindBuffer(gl.ARRAY_BUFFER, decalUvBuf);
        gl.vertexAttribPointer(aUv, 2, gl.FLOAT, false, 0, 0);
        gl.bindTexture(gl.TEXTURE_2D, decalTex);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, decalIdxBuf);
        gl.drawElements(gl.TRIANGLES, gridIdx.length, gl.UNSIGNED_SHORT, 0);
        gl.disable(gl.BLEND);
      }
    }

    /* --- cone physics (spec 5.1): inside 80% of the cone the drag is 1:1;
     *     past that the delta is damped toward x0.3 at the hard edge, with a
     *     rubber-band cap beyond it; release outside the cone eases back. --- */
    function dampAxis(cur, delta, min, max) {
      var next = cur + delta;
      var lim = delta > 0 ? max : min;             // signed bound being approached
      var soft = lim * 0.8;
      var outward = (lim > 0 && delta > 0) || (lim < 0 && delta < 0);
      if (outward && Math.abs(cur) > Math.abs(soft)) {
        var span = Math.abs(lim - soft) || 1;
        var f = clamp((Math.abs(cur) - Math.abs(soft)) / span, 0, 1);
        next = cur + delta * (1 - 0.7 * f);        // 1 -> 0.3 across the outer 20%
      }
      var cap = lim * 1.22;                        // rubber-band overshoot limit
      return lim > 0 ? Math.min(next, cap) : Math.max(next, cap);
    }
    var easeAnim = null;
    function easeBackIfNeeded() {
      var c = coneClampRel(station, state.relYaw, state.relPitch);
      if (c.relYaw === state.relYaw && c.relPitch === state.relPitch) return;
      easeAnim = { t0: (global.performance && performance.now) ? performance.now() : Date.now(),
                   fromYaw: state.relYaw, fromPitch: state.relPitch,
                   toYaw: c.relYaw, toPitch: c.relPitch };
      var step = function () {
        if (!easeAnim) return;
        var now = (global.performance && performance.now) ? performance.now() : Date.now();
        var k = clamp((now - easeAnim.t0) / 200, 0, 1);          // ~200 ms ease-back
        var e = 1 - Math.pow(1 - k, 3);                            // easeOutCubic
        state.relYaw = easeAnim.fromYaw + (easeAnim.toYaw - easeAnim.fromYaw) * e;
        state.relPitch = easeAnim.fromPitch + (easeAnim.toPitch - easeAnim.fromPitch) * e;
        draw();
        if (k < 1) global.requestAnimationFrame(step);
        else { easeAnim = null; emit(); }
      };
      global.requestAnimationFrame(step);
    }

    /* --- picking: pointer NDC -> ray -> panel planes (spec 4.4) --- */
    function pointerRay(e) {
      var r = canvas.getBoundingClientRect();
      var ndcX = ((e.clientX - r.left) / r.width) * 2 - 1;
      var ndcY = 1 - ((e.clientY - r.top) / r.height) * 2;
      var a = absView();
      return viewRayDir(a.yaw, a.pitch, state.fovY, r.width / r.height, ndcX, ndcY);
    }
    function decalLocalHit(hit) {
      // decal-local coords of a panel hit, undoing the in-plane rotation
      var lu = hit.u - state.uIn, lv = hit.v - state.vIn;
      var th = state.rotDeg * D2R, c = Math.cos(th), s = Math.sin(th);
      return { u: lu * c + lv * s, v: -lu * s + lv * c };
    }
    function insidePanelRect(panel, u, v) {
      return Math.abs(u) <= panel.widthIn / 2 && Math.abs(v) <= panel.heightIn / 2;
    }
    function clampCenterToPanel(panel, u, v) {
      return { u: clamp(u, -panel.widthIn / 2, panel.widthIn / 2),
               v: clamp(v, -panel.heightIn / 2, panel.heightIn / 2) };
    }

    /* --- input: drag split (spec D7) — drag the decal to move it, drag the
     *     background to look, tap a panel to move the decal there. Pinch and
     *     wheel-with-modifier zoom the FOV; plain wheel pans the view. --- */
    var pointers = new Map();
    var drag = null;   // {mode:'look'|'decal'|'maybeTap', lx, ly, downX, downY, downT, tapPanel, tapU, tapV}
    var pinch = null;  // {d0, fov0}

    function onDown(e) {
      try { canvas.setPointerCapture(e.pointerId); } catch (err) {}
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      easeAnim = null;
      if (pointers.size === 2) {
        var pts = Array.from(pointers.values());
        pinch = { d0: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y), fov0: state.fovY };
        drag = null;
        return;
      }
      var ray = pointerRay(e);
      var p = activePanel();
      var mode = 'look', tapPanel = null, tapU = 0, tapV = 0;
      if (p) {
        var hit = intersectPanel(ray, p);
        if (hit && insidePanelRect(p, hit.u, hit.v)) {
          var dl = decalLocalHit(hit);
          if (Math.abs(dl.u) <= state.wIn / 2 && Math.abs(dl.v) <= state.hIn / 2) mode = 'decal';
        }
      }
      if (mode === 'look') {
        for (var i = 0; i < panels.length; i++) {
          var h2 = intersectPanel(ray, panels[i]);
          if (h2 && insidePanelRect(panels[i], h2.u, h2.v)) {
            mode = 'maybeTap'; tapPanel = panels[i]; tapU = h2.u; tapV = h2.v; break;
          }
        }
      }
      drag = { mode: mode, lx: e.clientX, ly: e.clientY, downX: e.clientX, downY: e.clientY,
               downT: Date.now(), tapPanel: tapPanel, tapU: tapU, tapV: tapV };
      canvas.style.cursor = mode === 'decal' ? 'move' : 'grabbing';
    }
    function onMove(e) {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinch && pointers.size === 2) {
        var pts = Array.from(pointers.values());
        var d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        if (d > 0 && pinch.d0 > 0) {
          state.fovY = clamp(pinch.fov0 * pinch.d0 / d, FOV_MIN, FOV_MAX);
          draw(); emit();
        }
        return;
      }
      if (!drag) return;
      var dx = e.clientX - drag.lx, dy = e.clientY - drag.ly;
      if (drag.mode === 'maybeTap') {
        if (Math.hypot(e.clientX - drag.downX, e.clientY - drag.downY) > 7) drag.mode = 'look';
        else return;
      }
      drag.lx = e.clientX; drag.ly = e.clientY;
      if (drag.mode === 'decal') {
        var p = activePanel(); if (!p) return;
        var hit = intersectPanel(pointerRay(e), p);
        if (hit) {
          var cc = clampCenterToPanel(p, hit.u, hit.v);   // center stays on the panel;
          state.uIn = cc.u; state.vIn = cc.v;              // drag NEVER writes wIn (spec 4.4)
          rebuildDecal(); draw(); emit();
        }
      } else { // look
        var k = state.fovY / Math.max(1, canvas.clientHeight);  // 1:1 inside the cone
        state.relYaw = dampAxis(state.relYaw, -dx * k, station.cone.yawMin, station.cone.yawMax);
        state.relPitch = dampAxis(state.relPitch, dy * k, station.cone.pitchMin, station.cone.pitchMax);
        draw(); emit();
      }
    }
    function onUp(e) {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinch = null;
      if (drag && drag.mode === 'maybeTap' && pointers.size === 0) {
        var quick = Date.now() - drag.downT < 500;
        var still = Math.hypot(e.clientX - drag.downX, e.clientY - drag.downY) <= 7;
        if (quick && still && drag.tapPanel) {           // tap-to-place (spec 4.4)
          state.panelId = drag.tapPanel.id;
          var cc = clampCenterToPanel(drag.tapPanel, drag.tapU, drag.tapV);
          state.uIn = cc.u; state.vIn = cc.v;
          rebuildDecal(); draw(); emit();
        }
      }
      if (pointers.size === 0) {
        drag = null;
        canvas.style.cursor = 'grab';
        easeBackIfNeeded();
      } else if (drag) {                                  // pinch -> one finger left: rebase
        var rest = Array.from(pointers.values())[0];
        drag.lx = rest.x; drag.ly = rest.y; drag.mode = 'look';
      }
    }
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    canvas.addEventListener('wheel', function (e) {
      e.preventDefault();
      if (e.ctrlKey || e.metaKey) {                        // wheel-modifier = zoom
        state.fovY = clamp(state.fovY * (1 + e.deltaY * 0.001), FOV_MIN, FOV_MAX);
      } else {                                             // plain wheel = look (spec 3.4)
        state.relYaw += e.deltaX * 0.05;
        state.relPitch -= e.deltaY * 0.05;
        var c = coneClampRel(station, state.relYaw, state.relPitch);
        state.relYaw = c.relYaw; state.relPitch = c.relPitch;
      }
      draw(); emit();
    }, { passive: false });

    function onResize() { fitCanvas(); draw(); }
    global.addEventListener('resize', onResize);

    /* --- public API --- */
    function setSize(wIn) {
      if (!(wIn > 0)) return;
      state.wIn = wIn;
      state.hIn = wIn / decalAspect;    // height follows the design's aspect
      rebuildDecal(); draw(); emit();
    }
    function setPanel(panelId) {
      if (!panelById[panelId]) return false;
      state.panelId = panelId;
      state.uIn = 0; state.vIn = 0;      // re-centers on the new panel
      rebuildDecal(); draw(); emit();
      return true;
    }
    /* --- Phase 2 station system (spec 5.3): per-station placements + view.
     * Switching saves the live snapshot, then restores the target station's
     * snapshot; a first visit opens on the counterpart panel (same panel id
     * when the new station has it) at that panel's center, else its first
     * panel — never a pretend carry-over of a placement. --- */
    function snapshot() {
      return { panelId: state.panelId, uIn: state.uIn, vIn: state.vIn,
               rotDeg: state.rotDeg, wIn: state.wIn,
               relYaw: state.relYaw, relPitch: state.relPitch, fovY: state.fovY };
    }
    function applySnapshot(snap) {
      state.panelId = panelById[snap.panelId] ? snap.panelId : panels[0].id;
      state.uIn = snap.uIn || 0; state.vIn = snap.vIn || 0;
      state.rotDeg = snap.rotDeg || 0;
      if (snap.wIn > 0) { state.wIn = snap.wIn; state.hIn = snap.wIn / decalAspect; }
      state.relYaw = snap.relYaw || 0; state.relPitch = snap.relPitch || 0;
      if (snap.fovY > 0) state.fovY = snap.fovY;
      else if (opts.stationHome && opts.stationHome[station.id] && opts.stationHome[station.id].fovY > 0)
        state.fovY = opts.stationHome[station.id].fovY;
    }
    function setStation(stationId) {
      var entry = entryById[stationId];
      if (!entry || station.id === stationId) return false;
      var prevPanelId = state.panelId;
      savedByStation[station.id] = snapshot();
      curEntry = entry;
      station = entry.station;
      panels = station.panels.map(panelFromConfig);
      panelById = {};
      panels.forEach(function (p) { panelById[p.id] = p; });
      easeAnim = null; drag = null; pinch = null; pointers.clear();
      if (savedByStation[stationId]) {
        applySnapshot(savedByStation[stationId]);
      } else {
        var pid = panelById[prevPanelId] ? prevPanelId : panels[0].id;
        var cfgPanel = null;
        station.panels.forEach(function (cp) { if (cp.id === pid) cfgPanel = cp; });
        var c = cfgPanel ? panelCenterRel(station, cfgPanel) : { relYaw: 0, relPitch: 0 };
        var cl = coneClampRel(station, c.relYaw, c.relPitch);
        state.panelId = pid;
        state.uIn = 0; state.vIn = 0;
        state.relYaw = cl.relYaw; state.relPitch = cl.relPitch;
        var home = opts.stationHome && opts.stationHome[stationId];
        if (home && home.fovY > 0) state.fovY = home.fovY;
      }
      state.panoLoaded = !!(texCache[stationId] && texCache[stationId].tex);
      showPoster(entry.posterUrl);
      loadPano(entry);
      rebuildDecal(); draw(); emit();
      return true;
    }
    function setDesign(src, w, h) { setDecalSource(src, w, h); }
    function setFov(deg) {
      if (!(deg > 0)) return;
      state.fovY = clamp(deg, FOV_MIN, FOV_MAX);
      draw(); emit();
    }
    function getPlacement() {
      return { stationId: station.id, panelId: state.panelId,
               uIn: state.uIn, vIn: state.vIn, rotDeg: state.rotDeg, wIn: state.wIn };
    }
    function restorePlacement(pl) {
      if (!pl || !panelById[pl.panelId]) return false;
      state.panelId = pl.panelId;
      state.uIn = pl.uIn || 0; state.vIn = pl.vIn || 0; state.rotDeg = pl.rotDeg || 0;
      if (pl.wIn > 0) { state.wIn = pl.wIn; state.hIn = pl.wIn / decalAspect; }
      refreshFit(); rebuildDecal(); draw(); emit();
      return true;
    }
    function getState() {
      var p = activePanel(), a = absView();
      return { webgl: state.webgl, panoLoaded: state.panoLoaded,
        stationId: station.id, stationLabel: station.label || station.id,
        panelId: state.panelId, panelLabel: p ? p.label : state.panelId,
        uIn: state.uIn, vIn: state.vIn, rotDeg: state.rotDeg,
        wIn: state.wIn, hIn: state.hIn, fitOk: state.fitOk,
        viewYawRel: state.relYaw, viewPitchRel: state.relPitch,
        viewYawAbs: a.yaw, viewPitchAbs: a.pitch, fovY: state.fovY };
    }
    /* Spec 5.4: render the current frame at 2x CSS size and export it —
     * the decal is in the render, so export parity is automatic. */
    function exportPNG() {
      if (!state.webgl) return null;
      var cw = canvas.clientWidth || canvas.width, ch = canvas.clientHeight || canvas.height;
      canvas.width = Math.max(2, cw * 2); canvas.height = Math.max(2, ch * 2);
      draw();
      var url = null;
      try { url = canvas.toDataURL('image/png'); } catch (err) { url = null; }
      fitCanvas(); draw();
      return url;
    }
    function destroy() {
      global.removeEventListener('resize', onResize);
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    }

    if (opts.stationHome && opts.stationHome[station.id] && opts.stationHome[station.id].fovY > 0)
      state.fovY = opts.stationHome[station.id].fovY;
    loadPano(curEntry);
    rebuildDecal();
    fitCanvas();
    draw();
    emit();

    return { setSize: setSize, setPanel: setPanel, getState: getState,
             setStation: setStation, setDesign: setDesign, setFov: setFov,
             getPlacement: getPlacement, restorePlacement: restorePlacement,
             getStationId: function () { return station.id; },
             exportPNG: exportPNG, render: draw, destroy: destroy };
  }

  var api = { mount: mount, math: Math360 };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  global.BullardViewer360 = api;
})(typeof window !== 'undefined' ? window : globalThis);
